/**
 * AdminUpdatesManager — NECS Admin publisher for Updates & Notices.
 * Create/publish updates (title, body/video, effective date, category,
 * audiences, ack type) + per-update read/acknowledgment tracking.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Megaphone, Plus, Trash2, Users, X, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';
const inputCls = 'w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-[#C9A962] focus:outline-none';
const labelCls = 'text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block';

const CATEGORIES = [
  ['service_update', 'Service Update'],
  ['procedure_standards', 'Procedure & Standards'],
  ['important_notice', 'Important Notice'],
  ['new_knowledge', 'New Knowledge'],
  ['app_platform_update', 'App & Platform Update'],
  ['service_improvement', 'Service Improvement'],
  ['maintenance_notice', 'Maintenance Notice'],
  ['policy_update', 'Policy Update'],
];
const AUDIENCES = [
  ['residents', 'Residents'],
  ['property_managers', 'Property Managers'],
  ['field_staff', 'Field Staff'],
  ['commercial_clients', 'Commercial Clients'],
  ['all', 'All'],
];

const EMPTY = {
  title: '', body: '', video_url: '',
  effective_date: new Date().toISOString().slice(0, 10),
  category: 'service_update', audiences: ['all'], ack_type: 'read',
};

function TrackingDialog({ update, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { setData(await apiFetch(`/api/updates/${update.update_id}/tracking`)); }
      catch (_) { setData(false); }
    })();
  }, [update.update_id]);
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl border border-zinc-700 bg-[#141414] p-5" data-testid="update-tracking-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold break-words pr-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            TRACKING · {update.title}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white shrink-0" data-testid="tracking-close-btn"><X className="h-5 w-5" /></button>
        </div>
        {data === null && <p className="text-xs text-zinc-500">Loading…</p>}
        {data === false && <p className="text-xs text-red-400">Could not load tracking.</p>}
        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="rounded-xl border border-zinc-800 p-3 text-center">
                <p className="text-xl font-black text-white">{data.acked_count}</p>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">{update.ack_type === 'understand' ? 'Acknowledged' : 'Read'}</p>
              </div>
              <div className="rounded-xl border border-zinc-800 p-3 text-center">
                <p className="text-xl font-black text-white">{data.total_audience}</p>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">Audience size</p>
              </div>
              <div className="rounded-xl border border-zinc-800 p-3 text-center">
                <p className="text-xl font-black" style={{ color: GOLD }}>
                  {data.total_audience ? Math.round((data.acked_count / data.total_audience) * 100) : 0}%
                </p>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">Read rate</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-zinc-800 p-3">
                <p className={labelCls}>By role</p>
                {Object.entries(data.by_role || {}).map(([r, n]) => (
                  <p key={r} className="text-xs text-zinc-300 flex justify-between"><span className="capitalize">{r.replace(/_/g, ' ')}</span><span className="font-bold">{n}</span></p>
                ))}
                {Object.keys(data.by_role || {}).length === 0 && <p className="text-xs text-zinc-600">No acknowledgments yet</p>}
              </div>
              <div className="rounded-xl border border-zinc-800 p-3">
                <p className={labelCls}>By property</p>
                {Object.entries(data.by_property || {}).map(([p, n]) => (
                  <p key={p} className="text-xs text-zinc-300 flex justify-between"><span className="break-words">{p}</span><span className="font-bold">{n}</span></p>
                ))}
                {Object.keys(data.by_property || {}).length === 0 && <p className="text-xs text-zinc-600">No acknowledgments yet</p>}
              </div>
            </div>
            <p className={labelCls}>Per user ({(data.acknowledgments || []).length})</p>
            <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="tracking-user-list">
              {(data.acknowledgments || []).map((a) => (
                <div key={a.ack_id} className="rounded-lg border border-zinc-800 px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-white break-words">{a.user_name}</p>
                    <p className="text-[10px] text-zinc-500 capitalize">{(a.role || '').replace(/_/g, ' ')}{a.property_name ? ` · ${a.property_name}` : ''}</p>
                  </div>
                  <p className="text-[10px] text-zinc-500 shrink-0 flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3 text-emerald-400" /> {new Date(a.acked_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminUpdatesManager() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/updates/admin/all');
      setRows(d.updates || []);
    } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAud = (a) => setForm((f) => ({
    ...f,
    audiences: f.audiences.includes(a) ? f.audiences.filter((x) => x !== a) : [...f.audiences, a],
  }));

  const publish = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and content are required'); return; }
    if (form.audiences.length === 0) { toast.error('Pick at least one audience'); return; }
    setBusy(true);
    try {
      await apiFetch('/api/updates', { method: 'POST', body: { ...form, video_url: form.video_url || null } });
      toast.success('Update published');
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err.detail || 'Publish failed'); } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete "${u.title}"? Acknowledgment records will be removed too.`)) return;
    try { await apiFetch(`/api/updates/${u.update_id}`, { method: 'DELETE' }); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.detail || 'Delete failed'); }
  };

  return (
    <Card className="bg-zinc-900 border-l-4 border-l-[#C9A962] p-5" data-testid="admin-updates-manager">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" style={{ color: GOLD }} />
          <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            UPDATES &amp; NOTICES · PUBLISHER
          </h3>
        </div>
        <Button size="sm" className="font-bold" style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
          onClick={() => setShowForm((s) => !s)} data-testid="new-update-btn">
          <Plus className="h-4 w-4 mr-1" /> {showForm ? 'Close' : 'New Update'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={publish} className="rounded-xl border border-zinc-700 p-4 mb-4 space-y-3" data-testid="update-create-form">
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} data-testid="update-title-input" />
          </div>
          <div>
            <label className={labelCls}>Body content</label>
            <textarea className={`${inputCls} min-h-[110px]`} value={form.body} onChange={(e) => set('body', e.target.value)} data-testid="update-body-input" />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Video URL (optional)</label>
              <input className={inputCls} value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="https://youtube.com/…" data-testid="update-video-input" />
            </div>
            <div>
              <label className={labelCls}>Effective date</label>
              <input type="date" className={inputCls} value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} data-testid="update-date-input" />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)} data-testid="update-category-select">
                {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Audience</label>
            <div className="flex flex-wrap gap-2">
              {AUDIENCES.map(([k, l]) => (
                <button type="button" key={k} onClick={() => toggleAud(k)}
                  className="px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border"
                  style={form.audiences.includes(k)
                    ? { backgroundColor: 'rgba(201,169,98,0.12)', borderColor: GOLD, color: GOLD }
                    : { borderColor: '#3f3f46', color: '#71717a' }}
                  data-testid={`update-audience-${k}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Acknowledgment type</label>
            <div className="flex gap-2">
              {[['read', 'Mark as Read'], ['understand', 'I Understand (blocks navigation)']].map(([k, l]) => (
                <button type="button" key={k} onClick={() => set('ack_type', k)}
                  className="px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border"
                  style={form.ack_type === k
                    ? { backgroundColor: 'rgba(201,169,98,0.12)', borderColor: GOLD, color: GOLD }
                    : { borderColor: '#3f3f46', color: '#71717a' }}
                  data-testid={`update-acktype-${k}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy} className="font-bold w-full" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="update-publish-btn">
            Publish Update
          </Button>
        </form>
      )}

      <div className="space-y-2" data-testid="admin-updates-list">
        {rows.length === 0 && <p className="text-xs text-zinc-600 py-4 text-center">No updates published yet.</p>}
        {rows.map((u) => (
          <div key={u.update_id} className="rounded-xl border border-zinc-800 bg-black/30 p-3.5" data-testid={`admin-update-${u.update_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] font-black uppercase tracking-wider" style={{ color: GOLD }}>{u.category_label}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${u.ack_type === 'understand' ? 'bg-red-950/60 text-red-400 border border-red-900' : 'bg-zinc-800 text-zinc-400'}`}>
                    {u.ack_type === 'understand' ? 'I Understand' : 'Mark as Read'}
                  </span>
                  <span className="text-[10px] text-zinc-500">→ {(u.audiences || []).join(', ')}</span>
                </div>
                <p className="text-sm font-bold text-white mt-1 break-words">{u.title}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Effective {u.effective_date} · Published {new Date(u.created_at).toLocaleString()} · {u.ack_count} acknowledged</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setTracking(u)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800" title="Tracking" data-testid={`update-tracking-btn-${u.update_id}`}>
                  <Users className="h-4 w-4" />
                </button>
                <button onClick={() => remove(u)} className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-zinc-800" title="Delete" data-testid={`update-delete-btn-${u.update_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tracking && <TrackingDialog update={tracking} onClose={() => setTracking(null)} />}
    </Card>
  );
}
